import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
} from '@tanstack/react-query'

import { StandardError } from '@/types/api'

/**
 * Creates a strongly-typed React Query hook for queries (GET).
 *
 * @template TData - The type of the data returned by the query.
 * @template TPayload - The type of the payload sent to the query function.
 * @param {string} key - The unique key for the query.
 * @param {function} fn - The async function that fetches the data.
 * @returns {function} A custom hook that takes payload and options, returning a query.
 */
export const buildQuery = <TData, TPayload>(
  key: string,
  fn: (payload: TPayload) => Promise<TData>
) => {
  return (
    payload: TPayload,
    options?: Omit<
      UseQueryOptions<TData, StandardError>,
      'queryKey' | 'queryFn'
    >
  ) =>
    useQuery<TData, StandardError>({
      queryKey: [key, payload],
      queryFn: () => fn(payload),
      ...options,
    })
}

/**
 * Creates a strongly-typed React Query hook for queries that do not require a payload.
 *
 * @template TData - The type of the data returned by the query.
 * @param {string} key - The unique key for the query.
 * @param {function} fn - The async function that fetches the data.
 * @returns {function} A custom hook that takes options, returning a query.
 */
export const buildVoidQuery = <TData>(
  key: string,
  fn: () => Promise<TData>
) => {
  return (
    options?: Omit<
      UseQueryOptions<TData, StandardError>,
      'queryKey' | 'queryFn'
    >
  ) =>
    useQuery<TData, StandardError>({
      queryKey: [key],
      queryFn: fn,
      ...options,
    })
}

/**
 * Creates a strongly-typed React Query hook for mutations (POST, PUT, DELETE).
 *
 * @template TData - Tipo dos dados retornados.
 * @template TPayload - Tipo do payload da mutation.
 * @param {function} fn - Função async que executa a mutation.
 * @returns {function} Hook de mutation tipado.
 */
export const buildMutation = <TData, TPayload>(
  fn: (payload: TPayload) => Promise<TData>
) => {
  return (
    options?: Omit<
      UseMutationOptions<TData, StandardError, TPayload>,
      'mutationFn'
    >
  ) =>
    useMutation<TData, StandardError, TPayload>({
      mutationFn: fn,
      ...options,
    })
}
